// API utility functions for handling requests and token expiration

// In production with Nginx, we use /api as the base URL
// In development, we use the full URL from environment or default to localhost:8000
export const API_BASE = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

// API version prefix
export const API_VERSION = '/api/v1';

// Single request timeout (ms) the resilient client enforces on every backend
// call, so a backend that accepts the connection but never responds cannot pin
// a request forever. See REQ-FE-CLIENT-1 / REQ-FE-CORE-1.
export const REQUEST_TIMEOUT_MS = 15000;

/**
 * fetch bounded by REQUEST_TIMEOUT_MS via an AbortController.
 *
 * The returned promise always settles: when the timer fires it aborts the
 * in-flight request (so a real fetch rejects) and rejects with a typed
 * TimeoutError, raced against the fetch so even a fetch that never settles is
 * bounded.
 *
 * @param {string} url
 * @param {Object} options - Fetch options (a signal is merged in)
 * @returns {Promise<Response>}
 */
export const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  // Compose a caller-supplied signal (e.g. an unmount AbortController) with our
  // timeout controller, so either source aborts the request.
  if (options.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }
  let timer;
  const timeoutPromise = new Promise((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      const error = new Error(`Request timed out after ${REQUEST_TIMEOUT_MS}ms`);
      error.name = 'TimeoutError';
      reject(error);
    }, REQUEST_TIMEOUT_MS);
  });

  try {
    return await Promise.race([
      fetch(url, { ...options, signal: controller.signal }),
      timeoutPromise,
    ]);
  } finally {
    clearTimeout(timer);
  }
};

// Bounded, jittered transient retry (FR-15, REQ-FE-CLIENT-8): retries fire only
// for timeout / network-error / 5xx / 429, only for idempotent calls, at this one
// layer (not double-stacked with SWR).
export const MAX_RETRIES = 2;
const RETRY_BASE_MS = 300;
const RETRY_CAP_MS = 2000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Full jitter: random(0, min(cap, base * 2^attempt)).
const computeRetryBackoff = (attempt) =>
  Math.random() * Math.min(RETRY_CAP_MS, RETRY_BASE_MS * (2 ** attempt));

const isRetriableError = (error) => {
  if (!error) return false;
  if (error.name === 'TimeoutError') return true; // request timeout
  if (error instanceof TypeError) return true; // network-level failure
  return error.class === 'server' || error.class === 'throttle'; // 5xx / 429
};

/**
 * Get CSRF token from the backend
 * @returns {Promise<string>} - CSRF token
 */
export const getCsrfToken = async () => {
  try {
    // Check if we already have a token in memory
    let csrfToken = localStorage.getItem('csrfToken');

    // If no token exists or we're forcing a refresh, get a new one
    if (!csrfToken) {
      const response = await fetch(`${API_BASE}${API_VERSION}/tokens/csrf`, {
        credentials: 'include', // Important to include cookies
      });

      if (!response.ok) {
        throw new Error(`Failed to get CSRF token: ${response.status}`);
      }

      const data = await readJson(response);
      csrfToken = data.csrf_token;

      // Store token in localStorage for reuse
      localStorage.setItem('csrfToken', csrfToken);
    }

    return csrfToken;
  } catch (error) {
    console.error('Error getting CSRF token:', error);
    throw error;
  }
};

/**
 * Updates authentication tokens and dispatches an event for AuthContext
 * @param {string} access_token - The new access token
 * @param {string} refresh_token - The new refresh token
 * @param {number} expires_in - Expiry time in seconds
 * @returns {boolean} - Whether the update was successful
 */
export const updateTokens = (access_token, refresh_token, expires_in) => {
  localStorage.setItem('token', access_token);
  localStorage.setItem('refreshToken', refresh_token);
  localStorage.setItem('tokenExpiry', Date.now() + (expires_in * 1000));

  // Dispatch a custom event that AuthContext can listen for
  window.dispatchEvent(new CustomEvent('auth:tokens-updated', {
    detail: { access_token, refresh_token, expires_in }
  }));

  return true;
};

/**
 * Attempts to refresh the access token using the refresh token
 * @returns {Promise<boolean>} - Whether the refresh was successful
 */
export const refreshAccessToken = async () => {
  try {
    // Get stored refresh token
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      return false;
    }

    // Get CSRF token for the request
    let csrfToken;
    try {
      csrfToken = await getCsrfToken();
    } catch (error) {
      console.error('Failed to get CSRF token for refresh:', error);
      return false;
    }

    // Call refresh endpoint
    const response = await fetch(`${API_BASE}${API_VERSION}/tokens/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
      credentials: 'include'
    });

    if (!response.ok) {
      return false;
    }

    // Parse response and update stored tokens using the updateTokens function
    const data = await readJson(response);
    return updateTokens(data.access_token, data.refresh_token, data.expires_in);
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

/**
 * Custom fetch function that handles token expiration
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {Function} onTokenExpired - Callback function to execute when token expires
 * @returns {Promise} - Fetch promise
 */
export const fetchWithTokenExpiration = async (url, options = {}, onTokenExpired) => {
  try {
    // Check if token is about to expire (30 seconds buffer)
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (tokenExpiry && Date.now() > (parseInt(tokenExpiry) - 30000)) {
      // Proactively refresh token before it expires
      const refreshSuccess = await refreshAccessToken();
      if (!refreshSuccess) {
        // If refresh fails, trigger expiration callback
        if (onTokenExpired && typeof onTokenExpired === 'function') {
          onTokenExpired();
          throw new Error('Session expired. Please login again.');
        }
      }

      // Update Authorization header with new token
      if (options.headers && options.headers.Authorization) {
        options.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      }
    }

    // Make the API request (bounded by REQUEST_TIMEOUT_MS via AbortController)
    const response = await fetchWithTimeout(url, options);

    // Handle 401 Unauthorized errors
    if (response.status === 401 && !url.endsWith(`${API_VERSION}/tokens`)) {
      // Try to refresh the token
      const refreshSuccess = await refreshAccessToken();

      if (refreshSuccess) {
        // If refresh succeeded, retry the original request with new token
        const newOptions = { ...options };
        if (newOptions.headers && newOptions.headers.Authorization) {
          newOptions.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
        }
        return fetchWithTimeout(url, newOptions);
      } else {
        // If refresh failed, trigger expiration callback
        if (onTokenExpired && typeof onTokenExpired === 'function') {
          onTokenExpired();
        }
      }
    }

    // Typed-error seam (FR-10, REQ-FE-CLIENT-6): on a non-ok response, reject
    // with the typed { status, message, ... } from parseErrorResponse instead of
    // returning a raw Response, so every caller sees one error shape.
    if (!response.ok) {
      throw await parseErrorResponse(response);
    }

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Makes an authenticated API request with CSRF protection
 * @param {string} endpoint - API endpoint (without base URL and version prefix)
 * @param {Object} options - Fetch options
 * @param {Function} onTokenExpired - Callback function to execute when token expires
 * @returns {Promise} - Fetch promise
 */
export const apiRequest = async (endpoint, options = {}, onTokenExpired) => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('No authentication token found');
  }

  // Create headers object with Authorization token
  let headers = {
    ...(options.headers || {}),
    Authorization: `Bearer ${token}`
  };

  // For state-changing methods (not GET or HEAD), add CSRF token
  const method = options.method || 'GET';
  if (!['GET', 'HEAD'].includes(method.toUpperCase())) {
    try {
      const csrfToken = await getCsrfToken();
      headers['X-CSRF-Token'] = csrfToken;
    } catch (error) {
      // Fail closed (FR-14, REQ-FE-CLIENT-7): never send a state-changing request
      // without its CSRF header. A security control must not degrade to a
      // permissive send when the token cannot be obtained.
      console.error('Failed to add CSRF token to request:', error);
      const csrfError = new Error('CSRF token unavailable; request not sent');
      csrfError.name = 'CsrfUnavailableError';
      throw csrfError;
    }
  }

  // Create a new options object with the merged headers
  const mergedOptions = {
    ...options,
    headers,
    credentials: 'include', // Important to include cookies for CSRF validation
  };

  const url = `${API_BASE}${API_VERSION}${endpoint}`;

  // Only retry idempotent calls: GET/HEAD, or a side-effecting call carrying an
  // Idempotency-Key (FR-09), so a retry cannot double-submit (FR-15).
  const isIdempotent =
    ['GET', 'HEAD'].includes(method.toUpperCase()) || headers['Idempotency-Key'] != null;

  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      // return await so a rejection is caught here for the retry decision.
      // eslint-disable-next-line no-await-in-loop
      return await fetchWithTokenExpiration(url, mergedOptions, onTokenExpired);
    } catch (error) {
      lastError = error;
      // 4xx (incl. the 401-refresh path's outcome) and non-idempotent calls are
      // never retried; only timeout/network/5xx/429 on idempotent calls are.
      if (!isIdempotent || attempt >= MAX_RETRIES || !isRetriableError(error)) {
        throw error;
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(computeRetryBackoff(attempt));
    }
  }
  throw lastError;
};

/**
 * Initializes CSRF protection by fetching a token
 * Call this function when the app starts
 */
export const initCsrfProtection = async () => {
  try {
    await getCsrfToken();
    return true;
  } catch (error) {
    console.error('Failed to initialize CSRF protection:', error);
    return false;
  }
};

/**
 * Parse error response, supporting RFC 7807 Problem Details format
 * @param {Response} response - Fetch Response object
 * @returns {Promise<{message: string, status: number, type: string|null}>}
 */
export const parseErrorResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  const status = response.status;

  // Classify the failure so consumers can branch (FR-12, REQ-FE-DEG-3):
  // 429 -> throttle (back off, honour Retry-After), 5xx -> server (transient,
  // keep stale), else -> client (user-fixable).
  const errorClass = status === 429
    ? 'throttle'
    : (Math.floor(status / 100) === 5 ? 'server' : 'client');
  const retryAfterRaw = response.headers.get('Retry-After');
  const retryAfter = retryAfterRaw != null ? Number(retryAfterRaw) : undefined;
  const base = { status, class: errorClass, retryAfter };

  try {
    if (contentType.includes('application/problem+json') || contentType.includes('application/json')) {
      const data = await response.json();
      // FastAPI 422 detail is an array of {loc,msg,type}; flatten it to a
      // readable per-field string and also expose a fieldErrors map, so no
      // consumer renders the raw array as a React child (FR-11, REQ-FE-DEG-4).
      if (Array.isArray(data.detail)) {
        const fieldErrors = {};
        const message = data.detail
          .map((d) => {
            const field = Array.isArray(d.loc) ? d.loc.at(-1) : (d.loc ?? 'field');
            fieldErrors[field] = d.msg;
            return `${field}: ${d.msg}`;
          })
          .join('; ');
        return { message, type: data.type || null, fieldErrors, ...base };
      }
      // RFC 7807 format has 'detail' and 'title'
      const message = data.detail || data.title || data.message || 'An error occurred';
      return { message, type: data.type || null, ...base };
    }
    // Plain text error
    const text = await response.text();
    return { message: text || 'An error occurred', type: null, ...base };
  } catch (e) {
    return { message: 'An error occurred', type: null, ...base };
  }
};

export const NON_JSON_RESPONSE_MESSAGE =
  'The service is temporarily unavailable. Please try again shortly.';

const nonJsonResponseError = (response, contentType) => {
  const error = new Error(NON_JSON_RESPONSE_MESSAGE);
  error.name = 'NonJsonResponseError';
  error.status = response.status;
  error.class = 'server';
  error.type = null;
  error.contentType = contentType;
  return error;
};

/**
 * Read a successful response's JSON body behind a content-type guard
 * (REQ-FE-CLIENT-6; the companion to parseErrorResponse).
 *
 * `response.ok` is a STATUS check only. A proxy in front of the backend — the
 * maintenance nginx during a deploy, a captive portal, any misrouted edge — can
 * answer 200 with an HTML body. `response.json()` then throws a raw
 * `SyntaxError: Unexpected token '<', "<!DOCTYPE "... is not valid JSON`, which
 * is neither classifiable nor showable to a user, and which several screens
 * rendered verbatim into an error banner (DASS-3814).
 *
 * Treat it as the transient backend-unavailable condition it actually is, using
 * parseErrorResponse's own vocabulary: class 'server', which isRetriableError
 * already treats as retriable and statisticsErrorMessage already maps to a
 * "temporarily unavailable" message. No consumer needs to change to benefit.
 *
 * @param {Response} response - an already-ok Response
 * @returns {Promise<any>} the parsed JSON body
 * @throws {Error} typed { name, message, status, class: 'server', contentType }
 */
export const readJson = async (response) => {
  // A real Response always has headers; guard anyway so a malformed stub or an
  // exotic polyfill surfaces the typed error rather than a bare TypeError.
  const contentType =
    (response.headers && typeof response.headers.get === 'function'
      ? response.headers.get('content-type')
      : '') || '';
  if (!contentType.includes('json')) {
    throw nonJsonResponseError(response, contentType);
  }
  try {
    return await response.json();
  } catch (error) {
    // Content-type claimed JSON but the body will not parse (a truncated proxy
    // response, or HTML under a mislabelled header): same transient condition,
    // and still never a raw SyntaxError reaching a caller.
    throw nonJsonResponseError(response, contentType);
  }
};

const apiUtils = {
  API_BASE,
  API_VERSION,
  fetchWithTokenExpiration,
  apiRequest,
  getCsrfToken,
  refreshAccessToken,
  updateTokens,
  initCsrfProtection,
  parseErrorResponse,
  readJson
};

export default apiUtils;
